import React, { useState, useEffect, useRef, useLayoutEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { PainCardViz } from './PainViz';
import './Pain.css';

const MOBILE_MQ = '(max-width: 600px)';

const problems = [
  {
    title: 'Missed calls with no follow-up',
    body: "Someone called. No one answered. They moved on. Most businesses have nothing in place to recover that.",
  },
  {
    title: 'Slow response to new inquiries',
    body: "The first business to respond usually wins the client. Hours later means you've already lost most of them.",
  },
  {
    title: 'Staff stuck on manual admin',
    body: "Reminders, follow-ups, document requests. Your team is spending hours every week on work that shouldn't require a person.",
  },
  {
    title: 'Inquiries that go cold',
    body: "Someone was interested, then went quiet. Without a follow-up system, they're gone. With one, a lot of them come back.",
  },
  {
    title: 'Past clients who never return',
    body: "The people already in your system are your easiest source of new revenue. If no one's reaching out, you're leaving money behind.",
  },
  {
    title: "Tools that don't talk to each other",
    body: "Booking system, CRM, spreadsheet. None connected. Everything requires someone to remember to do it manually.",
  },
];

function PainProblemCard({ index, title, body }) {
  return (
    <article className="pain-problem-card">
      <div className="pain-problem-card__viz">
        <div className="pain-num-tag">{String(index + 1).padStart(2, '0')}</div>
        <PainCardViz index={index} />
      </div>
      <div className="pain-problem-card__body">
        <h3 className="pain-problem-card__title">{title}</h3>
        <p className="pain-problem-card__desc">{body}</p>
      </div>
    </article>
  );
}

const Pain = () => {
  const [ref, visible] = useScrollReveal();
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(MOBILE_MQ).matches
  );
  const [slide, setSlide] = useState(0);
  const [carouselCardMinHeight, setCarouselCardMinHeight] = useState(null);
  const touchStartX = useRef(null);
  const carouselRef = useRef(null);
  const sizerRef = useRef(null);

  useEffect(() => {
    const mq = window.matchMedia(MOBILE_MQ);
    const sync = () => setIsMobile(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  const measureCarouselCardHeights = useCallback(() => {
    if (!isMobile || !sizerRef.current) return;
    const slides = sizerRef.current.querySelectorAll('.pain-carousel-sizer-slide');
    let max = 0;
    slides.forEach((el) => {
      max = Math.max(max, el.offsetHeight);
    });
    if (max > 0) setCarouselCardMinHeight(max);
  }, [isMobile]);

  useLayoutEffect(() => {
    measureCarouselCardHeights();
  }, [measureCarouselCardHeights]);

  useEffect(() => {
    if (!isMobile || !carouselRef.current) return undefined;
    const ro = new ResizeObserver(() => measureCarouselCardHeights());
    ro.observe(carouselRef.current);
    return () => ro.disconnect();
  }, [isMobile, measureCarouselCardHeights]);

  const goPrev = () => {
    setSlide((s) => (s - 1 + problems.length) % problems.length);
  };

  const goNext = () => {
    setSlide((s) => (s + 1) % problems.length);
  };

  const onTouchStart = (e) => {
    touchStartX.current = e.changedTouches[0].clientX;
  };

  const onTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    touchStartX.current = null;
    if (dx > 48) goPrev();
    else if (dx < -48) goNext();
  };

  const current = problems[slide];

  return (
    <section id="pain" className="pain section">
      <div className="container">
        <div className="section-header pain-header">
          <p className="section-kicker pain-header__kicker">Where your revenue is going</p>
          <h2 className="section-title pain-header__title">
            Six things quietly <span className="pain-header__accent">costing you</span>
          </h2>
          <p className="section-subtitle">
            Not because demand is low. Because of what happens (or doesn&apos;t) after someone reaches out.
          </p>
        </div>

        {isMobile ? (
          <div
            ref={(node) => {
              carouselRef.current = node;
              ref.current = node;
            }}
            className={`pain-carousel reveal ${visible ? 'is-visible' : ''}`}
          >
            <div ref={sizerRef} className="pain-carousel-sizer" aria-hidden="true">
              {problems.map((p, i) => (
                <div key={p.title} className="pain-carousel-sizer-slide">
                  <PainProblemCard index={i} title={p.title} body={p.body} />
                </div>
              ))}
            </div>
            <div
              className="pain-carousel-viewport"
              role="region"
              aria-roledescription="carousel"
              aria-label="Common revenue leaks"
              aria-live="polite"
              onTouchStart={onTouchStart}
              onTouchEnd={onTouchEnd}
              style={
                carouselCardMinHeight != null
                  ? { '--pain-carousel-card-min-h': `${carouselCardMinHeight}px` }
                  : undefined
              }
            >
              <PainProblemCard key={current.title} index={slide} title={current.title} body={current.body} />
            </div>
            <div className="pain-carousel-controls">
              <button type="button" className="pain-carousel-btn" onClick={goPrev} aria-label="Previous problem">
                <ChevronLeft size={22} strokeWidth={2.25} />
              </button>
              <div className="pain-carousel-dots">
                {problems.map((p, i) => (
                  <button
                    key={p.title}
                    type="button"
                    aria-label={`${p.title} (${i + 1} of ${problems.length})`}
                    aria-current={i === slide ? 'true' : undefined}
                    className={`pain-carousel-dot ${i === slide ? 'is-active' : ''}`}
                    onClick={() => setSlide(i)}
                  />
                ))}
              </div>
              <button type="button" className="pain-carousel-btn" onClick={goNext} aria-label="Next problem">
                <ChevronRight size={22} strokeWidth={2.25} />
              </button>
            </div>
          </div>
        ) : (
          <div ref={ref} className={`pain-grid reveal ${visible ? 'is-visible' : ''}`}>
            {problems.map((p, i) => (
              <PainProblemCard key={p.title} index={i} title={p.title} body={p.body} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default Pain;
